import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useMsal } from "@azure/msal-react";
import { ROUTES } from "./shared/constant";
import SkypointLogoLoader from "./SkypointLogoLander/SkypointLogoLoader";
import { NoAccessCard } from "./NoAccessCard";
import { authService } from "../utils/authService";
import { loginRequest, silentTokenPolicy } from "../config/msalConfig";
import { signalAppReady } from "../utils/appLoadingSignal";
import { logger } from "../logger-init";
import { getAndClearOAuthReturnPath } from "../utils/oauthReturnHandler";
import type { SessionResponse } from "../common/App.service";

function Callback(): React.ReactElement {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();
  const { instance, inProgress, accounts } = useMsal();
  const hasAttemptedLogin = useRef(false);

  // Safety timeout: if MSAL hangs in startup/handleRedirect for more than
  // 10 seconds, force-navigate to home so the user isn't stuck forever.
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasAttemptedLogin.current) {
        logger.warn("[Callback] MSAL timed out, forcing navigation to home", { inProgress });
        hasAttemptedLogin.current = true;
        const savedInstanceId = authService.getSavedInstanceId();
        navigate(`${ROUTES.HOME}?instanceid=${savedInstanceId || ""}`, { replace: true });
      }
    }, 10_000);
    return () => clearTimeout(timeout);
  }, [navigate, inProgress]);

  useEffect(() => {
    if (inProgress === "startup" || inProgress === "handleRedirect") {
      logger.debug("[Callback] MSAL is processing redirect, waiting...", { inProgress });
      return;
    }

    if (hasAttemptedLogin.current) {
      return;
    }

    // Use MSAL accounts directly instead of Redux-dependent useMsalAuth hook.
    // After handleRedirectPromise resolves, accounts are populated but Redux
    // idTokenClaims may not be set yet (requires getAccessToken call).
    const account = accounts[0];
    logger.debug("[Callback] Checking user after MSAL ready", {
      account: account ? { id: account.localAccountId, name: account.name } : null,
      accountCount: accounts.length,
      inProgress,
    });

    if (!account) {
      logger.error("[Callback] No account found after MSAL processing");
      setErrorMessage("Authentication failed - no user found");
      setIsError(true);
      signalAppReady();
      return;
    }

    hasAttemptedLogin.current = true;

    // IMPORTANT: We intentionally bypass authService.getAccessToken() and
    // createAuthSession() here. Both can trigger full-page redirects on
    // failure (acquireTokenRedirect / errorHandler logout), which creates
    // an infinite redirect loop: login → /callback → 401 → logout → login.
    // Instead, we use acquireTokenSilent directly (no redirect fallback)
    // and call the session API without errorHandler (no logout on 401).
    const processLogin = async () => {
      // Step 1: Get access token — silent only, no redirect fallback
      let accessToken: string | null = null;
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          ...silentTokenPolicy,
          account,
        });
        accessToken = tokenResponse.accessToken;
      } catch (tokenError) {
        logger.warn("[Callback] Silent token acquisition failed", {
          error: tokenError instanceof Error ? tokenError.message : String(tokenError),
        });
      }

      // Step 2: Call session API directly — no errorHandler (avoids logout-on-401 loop)
      if (accessToken) {
        try {
          logger.debug("[Callback] Calling /api/user/session", {
            origin: window.location.origin,
            accountId: account.localAccountId,
          });

          const response = await fetch("/api/user/session", {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              "Accept": "application/json",
              "InstanceId": authService.getSavedInstanceId() || "",
            },
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const data: SessionResponse = await response.json();
            logger.debug("[Callback] session response", {
              isSuccess: data.isSuccess,
              instanceId: data.instanceId,
            });

            if (data.isSuccess) {
              // Use instanceId from session API directly — do NOT call
              // loadUserPreferences() here because it uses getAccessToken()
              // which falls back to acquireTokenRedirect(), creating another
              // redirect loop if silent token acquisition fails.
              const instanceId = data.instanceId || authService.getSavedInstanceId();
              if (instanceId) authService.saveInstanceId(instanceId);
              const returnResult = getAndClearOAuthReturnPath();
              const qs = instanceId ? `instanceid=${instanceId}` : "";
              const homeUrl = qs ? `${ROUTES.HOME}?${qs}` : ROUTES.HOME;
              const targetPath = returnResult?.path
                ? returnResult.path.includes("instanceid=")
                  ? returnResult.path
                  : qs
                    ? `${returnResult.path}${returnResult.path.includes("?") ? "&" : "?"}${qs}`
                    : returnResult.path
                : homeUrl;
              // If navigating to a deep link (e.g. ticket from email), inject home into
              // the history stack first so the browser back button returns to the dashboard
              // instead of the B2C OAuth pages (which show a Bad Request error).
              // Use replaceState directly (synchronous browser API) to avoid React Router
              // batching the two navigations into one, which would lose the home entry.
              if (targetPath !== homeUrl) {
                window.history.replaceState(null, "", homeUrl);
                navigate(targetPath);
              } else {
                navigate(targetPath, { replace: true });
              }
              return;
            }
          } else {
            logger.warn("[Callback] session API returned non-OK status", {
              status: response.status,
            });
          }
        } catch (fetchError) {
          logger.warn("[Callback] session API call failed", {
            error: fetchError instanceof Error ? fetchError.message : String(fetchError),
          });
        }
      }

      // Fallback: navigate to stored return path or home — the app init flow
      // (useAppInitialization) will handle instance resolution independently.
      const savedInstanceId = authService.getSavedInstanceId();
      const fallbackReturn = getAndClearOAuthReturnPath();
      const fallbackQs = savedInstanceId ? `instanceid=${savedInstanceId}` : "";
      const fallbackHomeUrl = fallbackQs ? `${ROUTES.HOME}?${fallbackQs}` : ROUTES.HOME;
      const fallbackPath = fallbackReturn?.path
        ? fallbackReturn.path.includes("instanceid=")
          ? fallbackReturn.path
          : fallbackQs
            ? `${fallbackReturn.path}${fallbackReturn.path.includes("?") ? "&" : "?"}${fallbackQs}`
            : fallbackReturn.path
        : fallbackHomeUrl;
      if (fallbackPath !== fallbackHomeUrl) {
        window.history.replaceState(null, "", fallbackHomeUrl);
        navigate(fallbackPath);
      } else {
        navigate(fallbackPath, { replace: true });
      }
    };

    processLogin();
  }, [navigate, instance, accounts, inProgress]);

  if (isError) {
    logger.debug("[Callback] Showing error card", { errorMessage });
    return <NoAccessCard />;
  }

  return <SkypointLogoLoader />;
}

export default Callback;
